import JSONSerializer from './JSONSerializer';
import {
  ContentWorkingCopy,
  WorkingCopyKey,
  InlineComment,
} from '@amzn/awscat-pattern-library-service-model';
import LocalStorage from './LocalStorage';
import { MplServiceClientFactory } from '../api/MplServiceClientFactory';
import { IMplServiceClient } from '../api/IMplServiceClient';
import { DateTime } from 'luxon';
import { SaveWorkingCopyError, ServiceCallError } from '../services/Errors';

export class ContentWorkingCopyManager {
  #mplService: IMplServiceClient;

  // Defaults to the legacy client so v1 viewers are unchanged; v2 (AutoSavingService)
  // injects the typed-error client so failures surface as ServiceCallError.
  constructor(mplService: IMplServiceClient = MplServiceClientFactory.create()) {
    this.#mplService = mplService;
  }

  /**
   * Persist a working copy to local storage and the backend.
   *
   * @throws {SaveWorkingCopyError} when the backend `postContentWorkingCopy` call fails.
   */
  public async createContentWorkingCopy(
    id: string,
    key: WorkingCopyKey,
    workingCopy: ContentWorkingCopy,
    uploadUpdatedComments: boolean = false,
  ) {
    const creationDate = DateTime.utc();
    if (uploadUpdatedComments) {
      const updatedComments = LocalStorage.getUpdatedComments(id);
      const updatedCommentArr: InlineComment[] = [];
      if (updatedComments) {
        Object.values(updatedComments).forEach((commentArr) => {
          if (Array.isArray(commentArr)) {
            commentArr.forEach((comment) => {
              comment.createdAt = DateTime.fromISO(comment.createdAt);
              updatedCommentArr.push(comment);
            });
          }
        });
      }
      workingCopy.updatedInlineComments = updatedCommentArr;
    }
    LocalStorage.setContentWorkingCopy(id, key, workingCopy, creationDate);
    try {
      await this.#mplService.postContentWorkingCopy(id, workingCopy, creationDate, key);
    } catch (err) {
      throw err instanceof ServiceCallError
        ? SaveWorkingCopyError.fromServiceCall(err)
        : new SaveWorkingCopyError(err);
    }
  }

  /**
   * Return the newer of the local and backend working copies for the content.
   *
   * @throws {ServiceCallError} when the backend call fails.
   */
  public async getMostRecentWorkingCopy(
    contentId: string,
    key: WorkingCopyKey,
  ): Promise<{ workingCopy: ContentWorkingCopy; createdAt: DateTime }> {
    const lwcInfo = LocalStorage.getContentWorkingCopy(contentId, key);

    const listResult = await this.#mplService.listContentWorkingCopies(contentId, key, 1);
    if (listResult.workingCopies.length === 0) {
      // @ts-expect-error TS2322 - strict migration
      return lwcInfo;
    }

    const rwcInfo = listResult.workingCopies[0];

    // @ts-expect-error TS18048 - strict migration
    if (!lwcInfo || rwcInfo?.createdAt > lwcInfo?.createdAt) {
      const getResult = await this.#mplService.getContentWorkingCopy(
        contentId,
        key,
        // @ts-expect-error TS2532 - strict migration
        listResult.workingCopies[0].digest,
      );
      // @ts-expect-error TS18048 - strict migration
      return { workingCopy: getResult.workingCopy, createdAt: rwcInfo.createdAt };
    } else {
      return lwcInfo;
    }
  }

  /**
   * Remove the working copies for the content from local storage and the backend.
   *
   * @throws {ServiceCallError} when the backend call fails.
   */
  public async deleteWorkingCopies(contentId: string, key: WorkingCopyKey) {
    LocalStorage.removeContentWorkingCopy(contentId, key);
    LocalStorage.removeUpdatedComments(contentId);
    await this.#mplService.deleteContentWorkingCopies(contentId, key);
  }
}
